import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { toast } from "sonner";
import {
  FileCheck, Plus, Search, Eye, Edit, Trash2, Send, ClipboardCheck,
  ChevronsUpDown, Check, Loader2, UploadCloud, X, FileText, History, RefreshCw
} from "lucide-react";
import ChecklistModal from "./ChecklistModal";
import HistorialChecklist from "./HistorialChecklist";

const EMPTY_FORM = {
  moneda: "RD$", sucursal: "", compania: "", cliente: "", poliza: "", ramo: "",
  status: "COTIZACIÓN", fecha_cotizado: "", fecha_riesgo_aceptado: "", vence_en: "",
  prima: 0, ejecutivo: "", clase_cliente: "", grupo_cliente: "", observaciones: "",
  supervisor: "", gestor_cobros: "", vendedor: "", tipo_poliza: "", linea: "",
};

export default function ControlCotizacionesPage() {
  const { user, getAuthHeaders } = useAuth();
  const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [vistaSupervisor, setVistaSupervisor] = useState("");
  const [vistaSupervisorOpciones, setVistaSupervisorOpciones] = useState([]);
  const avisoCoberturaRef = useRef(false);
  const [config, setConfig] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [companias, setCompanias] = useState([]);

  // Dialogs
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ejecutivoOpen, setEjecutivoOpen] = useState(false);
  const [companiaOpen, setCompaniaOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [checklistItem, setChecklistItem] = useState(null);
  const [transferItem, setTransferItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      let url = `${API_URL}/api/control-cotizaciones/?limit=200`;
      if (search) url += `&busqueda=${encodeURIComponent(search)}`;
      if (filterStatus === "TRANSFERIDA") url += `&transferida=true`;
      else if (filterStatus) url += `&status=${encodeURIComponent(filterStatus)}&transferida=false`;
      if (vistaSupervisor) url += `&vista_supervisor_id=${encodeURIComponent(vistaSupervisor)}`;
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setIsAdmin(!!data.is_admin);
    } catch (e) {
      toast.error("Error al cargar las cotizaciones");
    }
  }, [API_URL, getAuthHeaders, search, filterStatus, vistaSupervisor]);

  const fetchVistaSupervisorOpciones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/control-cotizaciones/vista-supervisor/opciones`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setVistaSupervisorOpciones(data.opciones || []);
        if (data.por_delegacion && (data.opciones || []).length > 0 && !avisoCoberturaRef.current) {
          avisoCoberturaRef.current = true;
          const nombres = (data.opciones || []).map((s) => s.name).join(", ");
          toast.info(`Tienes una Cobertura de Supervisor activa: ${nombres}. Usa el filtro "Vista Supervisor" para ver su información.`, { duration: 9000 });
        }
      }
    } catch (e) { /* silent */ }
  }, [API_URL, getAuthHeaders]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/control-renovaciones/config/all`, getAuthHeaders());
      setConfig(await res.json());
    } catch (e) { /* silent */ }
  }, [API_URL, getAuthHeaders]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, getAuthHeaders());
      const data = await res.json();
      const us = (data.users || data || []).filter(u => u.activo !== false)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setUsuarios(us);
    } catch (e) { /* silent */ }
  }, [API_URL, getAuthHeaders]);

  const fetchCompanias = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/control-cotizaciones/companias`, getAuthHeaders());
      const data = await res.json();
      setCompanias(data.companias || []);
    } catch (e) { /* silent */ }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchItems(), fetchConfig(), fetchUsuarios(), fetchCompanias(), fetchVistaSupervisorOpciones()]);
      setLoading(false);
    };
    load();
  }, [fetchItems, fetchConfig, fetchUsuarios, fetchCompanias]);

  const openNew = () => { setFormData(EMPTY_FORM); setEditMode(false); setEditId(null); setShowForm(true); };
  const openEdit = (item) => {
    setFormData({ ...EMPTY_FORM, ...item, prima: item.prima || 0 });
    setEditMode(true); setEditId(item.id); setShowForm(true);
  };

  const handleSubmit = async () => {
    const statusU = (formData.status || "").toUpperCase();
    const missing = [];
    if (statusU === "COTIZACIÓN" && !formData.fecha_cotizado) missing.push("Fecha Cotizado");
    if (statusU === "RIESGO ACEPTADO" && !formData.fecha_riesgo_aceptado) missing.push("Fecha Riesgo Aceptado");
    if (missing.length) { toast.error(`Campos obligatorios: ${missing.join(", ")}`); return; }

    setSaving(true);
    try {
      const url = editMode
        ? `${API_URL}/api/control-cotizaciones/${editId}`
        : `${API_URL}/api/control-cotizaciones/`;
      const res = await fetch(url, {
        method: editMode ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success(editMode ? "Cotización actualizada" : "Cotización creada");
        setShowForm(false);
        fetchItems();
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || "Error al guardar";
        // Cobertura de Supervisor: el backend exige nota de soporte del cambio
        if (typeof detail === "string" && detail.startsWith("NOTA_DELEGACION_REQUERIDA")) {
          const nota = window.prompt("Cobertura de Supervisor: estás modificando una cotización del supervisor que cubres.\n\nEscribe la nota de soporte que explique este cambio (obligatoria para guardar):");
          if (nota && nota.trim()) {
            const res2 = await fetch(url, {
              method: "PUT",
              headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
              body: JSON.stringify({ ...formData, nota_delegacion: nota.trim() }),
            });
            if (res2.ok) {
              toast.success("Cotización actualizada (nota de soporte registrada)");
              setShowForm(false);
              fetchItems();
            } else {
              const e2 = await res2.json().catch(() => ({}));
              toast.error(e2.detail || "Error al guardar");
            }
          } else {
            toast.error("Cambio no guardado: la nota de soporte es obligatoria");
          }
        } else {
          toast.error(detail);
        }
      }
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await fetch(`${API_URL}/api/control-cotizaciones/${deleteItem.id}`, {
        method: "DELETE", ...getAuthHeaders(),
      });
      if (res.ok) { toast.success("Cotización eliminada"); fetchItems(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error al eliminar"); }
    } catch (e) { toast.error("Error al eliminar"); }
    setDeleteItem(null);
  };

  const statusBadge = (item) => {
    if (item.transferida) return <Badge className="bg-emerald-600 text-white" data-testid={`badge-transferida-${item.id}`}>Transferida</Badge>;
    const s = (item.status || "").toUpperCase();
    if (s === "RIESGO ACEPTADO") return <Badge className="bg-indigo-600 text-white">Riesgo Aceptado</Badge>;
    return <Badge className="bg-amber-500 text-white">Cotización</Badge>;
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 min-w-0 ml-0 md:ml-64 p-4 md:p-6" data-testid="control-cotizaciones-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-600 to-indigo-600 flex items-center justify-center shadow-lg">
            <FileCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Control de Cotizaciones/Emisiones</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Gestión y transferencia de cotizaciones a emisión</p>
          </div>
        </div>

        <Tabs defaultValue="cotizaciones">
          <TabsList className="mb-4">
            <TabsTrigger value="cotizaciones" data-testid="tab-cotizaciones" className="flex items-center gap-2">
              <FileCheck className="w-4 h-4" /> Cotizaciones/Emisiones
            </TabsTrigger>
            <TabsTrigger value="historial-checklist" data-testid="tab-historial-checklist-cot" className="flex items-center gap-2">
              <History className="w-4 h-4" /> Historial Checklist
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cotizaciones">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs">Buscar</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Cliente, póliza, ejecutivo, compañía…" className="pl-8" data-testid="cot-search" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={filterStatus || "ALL"} onValueChange={(v) => setFilterStatus(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-48" data-testid="cot-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Todos</SelectItem>
                      <SelectItem value="COTIZACIÓN">Cotización</SelectItem>
                      <SelectItem value="RIESGO ACEPTADO">Riesgo Aceptado</SelectItem>
                      <SelectItem value="TRANSFERIDA">Transferida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {vistaSupervisorOpciones.length > 0 && (
                  <div>
                    <Label className="text-xs">Vista Supervisor</Label>
                    <Select value={vistaSupervisor || "PROPIA"} onValueChange={(v) => setVistaSupervisor(v === "PROPIA" ? "" : v)}>
                      <SelectTrigger className="w-52" data-testid="cot-vista-supervisor-select"><SelectValue placeholder="Mi vista" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PROPIA">Mi vista</SelectItem>
                        {vistaSupervisorOpciones.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`cot-vista-supervisor-opcion-${s.id}`}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {vistaSupervisor && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 h-9 flex items-center" data-testid="cot-badge-vista-supervisor">
                    Viendo: {vistaSupervisorOpciones.find((s) => s.id === vistaSupervisor)?.name || "Supervisor"}
                  </Badge>
                )}
                <Button variant="outline" onClick={fetchItems} data-testid="cot-refresh-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
                </Button>
                <Button onClick={openNew} data-testid="cot-nueva-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nueva Cotización
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-auto rounded-lg max-h-[65vh]">
                  <table className="w-full text-sm" data-testid="cot-tabla">
                    <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10">
                      <tr>
                        {["Cliente", "Póliza", "Ramo", "Compañía", "Sucursal", "Vence En", "Prima", "Ejecutivo", "Creado Por", "Status", "Checklist", "Acciones"].map(h => (
                          <th key={h} className={`px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 ${h === "Cliente" ? "max-lg:sticky max-lg:left-0 max-lg:z-20" : ""}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</td></tr>
                      ) : items.length === 0 ? (
                        <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400" data-testid="cot-sin-datos">Sin cotizaciones registradas</td></tr>
                      ) : items.map((it) => {
                        const puedeEditar = isAdmin || !it.transferida;
                        const puedeTransferir = !it.transferida;
                        return (
                          <tr key={it.id} className="border-t dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`cot-row-${it.id}`}>
                            <td className="px-3 py-2 font-medium max-lg:sticky max-lg:left-0 max-lg:z-[1] max-lg:bg-white dark:max-lg:bg-slate-900" title={it.nota_delegacion_ultima || undefined}>
                              {it.cliente}
                              {it.nota_delegacion_ultima && (
                                <span className="ml-1.5 inline-block align-middle text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-300 cursor-help" data-testid={`nota-delegacion-cot-${it.id}`} title={it.nota_delegacion_ultima}>Nota de cobertura</span>
                              )}
                            </td>
                            <td className="px-3 py-2">{it.poliza || <span className="text-slate-400 italic">En Proceso</span>}</td>
                            <td className="px-3 py-2">{it.ramo}</td>
                            <td className="px-3 py-2">{it.compania}</td>
                            <td className="px-3 py-2">{it.sucursal}</td>
                            <td className="px-3 py-2">{it.vence_en}{it.dia_vencimiento ? ` (${it.dia_vencimiento})` : ""}</td>
                            <td className="px-3 py-2">{it.moneda} {(it.prima || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2">{it.ejecutivo}</td>
                            <td className="px-3 py-2" data-testid={`cot-creado-por-${it.id}`}>{it.created_by_name || <span className="text-slate-400 text-xs">—</span>}</td>
                            <td className="px-3 py-2">{statusBadge(it)}{it.ticket_numero ? <span className="block text-[10px] text-slate-500 mt-0.5">Ticket #{it.ticket_numero}</span> : null}</td>
                            <td className="px-3 py-2">
                              {it.checklist_hecho ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">✓ Checklist</Badge>
                              ) : <span className="text-slate-400 text-xs">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" title="Ver" onClick={() => setViewItem(it)} data-testid={`cot-ver-${it.id}`}>
                                  <Eye className="w-4 h-4 text-slate-500" />
                                </Button>
                                {puedeEditar && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Editar" onClick={() => openEdit(it)} data-testid={`cot-editar-${it.id}`}>
                                    <Edit className="w-4 h-4 text-blue-600" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-7 w-7" title="Checklist" onClick={() => setChecklistItem(it)} data-testid={`cot-checklist-${it.id}`}>
                                  <ClipboardCheck className={`w-4 h-4 ${it.checklist_hecho ? "text-green-600" : "text-amber-600"}`} />
                                </Button>
                                {puedeTransferir && (
                                  it.checklist_hecho ? (
                                    <Button size="icon" variant="ghost" className="h-7 w-7" title="Transferir" onClick={() => setTransferItem(it)} data-testid={`cot-transferir-${it.id}`}>
                                      <Send className="w-4 h-4 text-indigo-600" />
                                    </Button>
                                  ) : (
                                    <Button size="icon" variant="ghost" className="h-7 w-7 opacity-40 cursor-not-allowed" title="Debes realizar el checklist antes de transferir"
                                      onClick={() => toast.error("Debes realizar el checklist de coberturas antes de transferir")} data-testid={`cot-transferir-bloqueado-${it.id}`}>
                                      <Send className="w-4 h-4 text-slate-400" />
                                    </Button>
                                  )
                                )}
                                {isAdmin && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Eliminar" onClick={() => setDeleteItem(it)} data-testid={`cot-eliminar-${it.id}`}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 text-xs text-slate-500 border-t dark:border-slate-700">Total: {total}</div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historial-checklist">
            <HistorialChecklist API_URL={API_URL} getAuthHeaders={getAuthHeaders} canEdit={isAdmin} currentUser={user} origen="cotizaciones" />
          </TabsContent>
        </Tabs>

        {/* Formulario Nueva/Editar Cotización */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Editar Cotización" : "Nueva Cotización"}</DialogTitle>
            </DialogHeader>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5 text-[11px] text-amber-800 dark:text-amber-200 -mb-1">
              Solo la fecha según el status es obligatoria al guardar. Los demás campos serán obligatorios al momento de <b>transferir</b>.
            </div>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Moneda</Label>
                <Select value={formData.moneda} onValueChange={(v) => setFormData({ ...formData, moneda: v })}>
                  <SelectTrigger data-testid="cot-form-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>{config?.monedas?.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sucursal</Label>
                <Select value={formData.sucursal} onValueChange={(v) => setFormData({ ...formData, sucursal: v })}>
                  <SelectTrigger data-testid="cot-form-sucursal"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{(config?.sucursales || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compañía</Label>
                <Popover open={companiaOpen} onOpenChange={setCompaniaOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="cot-form-compania">
                      <span className="truncate">{formData.compania || "Seleccionar aseguradora..."}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar aseguradora..." />
                      <CommandList>
                        <CommandEmpty>No se encontró la aseguradora.</CommandEmpty>
                        <CommandGroup>
                          {companias.map((c) => (
                            <CommandItem key={c} value={c}
                              onSelect={() => { setFormData({ ...formData, compania: c }); setCompaniaOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.compania === c ? "opacity-100" : "opacity-0"}`} />
                              {c}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Cliente</Label>
                <Input value={formData.cliente} onChange={(e) => setFormData({ ...formData, cliente: e.target.value })} data-testid="cot-form-cliente" />
              </div>
              <div>
                <Label>Póliza</Label>
                <Input value={formData.poliza || ""} onChange={(e) => setFormData({ ...formData, poliza: e.target.value })} placeholder="Opcional (En Proceso)" data-testid="cot-form-poliza" />
              </div>
              <div>
                <Label>Ramo</Label>
                <Select value={formData.ramo} onValueChange={(v) => setFormData({ ...formData, ramo: v })}>
                  <SelectTrigger data-testid="cot-form-ramo"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{(config?.ramos || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="cot-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COTIZACIÓN">Cotización</SelectItem>
                    <SelectItem value="RIESGO ACEPTADO">Riesgo Aceptado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.status || "").toUpperCase() === "COTIZACIÓN" && (
                <div data-testid="cot-fecha-cotizado-wrapper">
                  <Label>Fecha Cotizado <span className="text-rose-600">*</span></Label>
                  <Input type="date" value={formData.fecha_cotizado || ""}
                    onChange={(e) => setFormData({ ...formData, fecha_cotizado: e.target.value })}
                    data-testid="cot-fecha-cotizado" required />
                </div>
              )}
              {(formData.status || "").toUpperCase() === "RIESGO ACEPTADO" && (
                <div data-testid="cot-fecha-riesgo-wrapper">
                  <Label>Fecha Riesgo Aceptado <span className="text-rose-600">*</span></Label>
                  <Input type="date" value={formData.fecha_riesgo_aceptado || ""}
                    onChange={(e) => setFormData({ ...formData, fecha_riesgo_aceptado: e.target.value })}
                    data-testid="cot-fecha-riesgo" required />
                </div>
              )}
              <div>
                <Label>Vence En / Fecha Emisión</Label>
                <Input type="date"
                  value={/^\d{4}-\d{2}-\d{2}$/.test(formData.vence_en || "") ? formData.vence_en : ""}
                  placeholder={formData.vence_en || ""}
                  onChange={(e) => setFormData({ ...formData, vence_en: e.target.value })}
                  data-testid="cot-form-vence" />
                {formData.vence_en && !/^\d{4}-\d{2}-\d{2}$/.test(formData.vence_en) && (
                  <p className="text-[11px] text-slate-500 mt-0.5">Actual: {formData.vence_en}{formData.dia_vencimiento ? ` (día ${formData.dia_vencimiento})` : ""}</p>
                )}
              </div>
              <div>
                <Label>Prima</Label>
                <Input type="number" value={formData.prima}
                  onChange={(e) => setFormData({ ...formData, prima: parseFloat(e.target.value) || 0 })} data-testid="cot-form-prima" />
              </div>
              <div>
                <Label>Ejecutivo</Label>
                <Popover open={ejecutivoOpen} onOpenChange={setEjecutivoOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between" data-testid="cot-form-ejecutivo">
                      {formData.ejecutivo || "Seleccionar ejecutivo..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar ejecutivo..." />
                      <CommandList>
                        <CommandEmpty>No se encontró ejecutivo.</CommandEmpty>
                        <CommandGroup>
                          {usuarios.map((u) => (
                            <CommandItem key={u.id} value={u.name || u.email}
                              onSelect={(v) => { setFormData({ ...formData, ejecutivo: v }); setEjecutivoOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.ejecutivo === (u.name || u.email) ? "opacity-100" : "opacity-0"}`} />
                              {u.name || u.email}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Clase Cliente</Label>
                <Select value={formData.clase_cliente} onValueChange={(v) => setFormData({ ...formData, clase_cliente: v })}>
                  <SelectTrigger data-testid="cot-form-clase"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{config?.clases_cliente?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grupo Cliente</Label>
                <Input value={formData.grupo_cliente || ""} onChange={(e) => setFormData({ ...formData, grupo_cliente: e.target.value })} placeholder="Ej: Grupo ABC" data-testid="cot-form-grupo" />
              </div>

              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Datos Corporativo / Empresarial</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Supervisor</Label>
                    <Select value={formData.supervisor || ""} onValueChange={(v) => setFormData({ ...formData, supervisor: v })}>
                      <SelectTrigger data-testid="cot-form-supervisor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{(config?.lista_supervisores || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Gestor de Cobros</Label>
                    <Select value={formData.gestor_cobros || ""} onValueChange={(v) => setFormData({ ...formData, gestor_cobros: v })}>
                      <SelectTrigger data-testid="cot-form-gestor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{(config?.gestores_cobros || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Vendedor</Label>
                    <Select value={formData.vendedor || ""} onValueChange={(v) => setFormData({ ...formData, vendedor: v })}>
                      <SelectTrigger data-testid="cot-form-vendedor"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{(config?.vendedores || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de Póliza</Label>
                    <Select value={formData.tipo_poliza || ""} onValueChange={(v) => setFormData({ ...formData, tipo_poliza: v })}>
                      <SelectTrigger data-testid="cot-form-tipo-poliza"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{(config?.tipos_poliza || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Línea</Label>
                    <Select value={formData.linea || ""} onValueChange={(v) => setFormData({ ...formData, linea: v })}>
                      <SelectTrigger data-testid="cot-form-linea"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{(config?.lineas || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea value={formData.observaciones || ""} onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} data-testid="cot-form-observaciones" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="cot-form-guardar">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {editMode ? "Actualizar" : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ver Cotización */}
        <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Detalle de la Cotización</DialogTitle></DialogHeader>
            {viewItem && <ResumenCotizacion item={viewItem} />}
            <DialogFooter><Button variant="outline" onClick={() => setViewItem(null)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmar eliminación */}
        <Dialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>¿Eliminar cotización?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">Se eliminará la cotización de <b>{deleteItem?.cliente}</b>. Esta acción no se puede deshacer.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteItem(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="cot-confirmar-eliminar">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transferir */}
        <TransferirDialog
          item={transferItem}
          onClose={() => setTransferItem(null)}
          API_URL={API_URL}
          getAuthHeaders={getAuthHeaders}
          config={config}
          usuarios={usuarios}
          companias={companias}
          onTransferred={() => { setTransferItem(null); fetchItems(); }}
        />

        {/* Checklist */}
        <ChecklistModal
          open={!!checklistItem}
          onClose={() => setChecklistItem(null)}
          renovacion={checklistItem}
          API_URL={API_URL}
          getAuthHeaders={getAuthHeaders}
          currentUser={user}
          origen="cotizaciones"
          onSaved={() => { toast.success("Checklist guardado en el historial de cotizaciones"); fetchItems(); }}
        />
      </main>
    </div>
  );
}

function ResumenCotizacion({ item }) {
  const rows = [
    ["Cliente", item.cliente], ["Póliza", item.poliza || "En Proceso"], ["Ramo", item.ramo],
    ["Compañía", item.compania], ["Sucursal", item.sucursal], ["Moneda", item.moneda],
    ["Prima", (item.prima || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })],
    ["Vence En", `${item.vence_en || "—"}${item.dia_vencimiento ? ` (día ${item.dia_vencimiento})` : ""}`],
    ["Status", item.transferida ? "Transferida" : item.status],
    ["Fecha Cotizado", item.fecha_cotizado || "—"], ["Fecha Riesgo Aceptado", item.fecha_riesgo_aceptado || "—"],
    ["Ejecutivo", item.ejecutivo], ["Clase Cliente", item.clase_cliente], ["Grupo Cliente", item.grupo_cliente || "—"],
    ["Supervisor", item.supervisor || "—"], ["Gestor de Cobros", item.gestor_cobros || "—"],
    ["Vendedor", item.vendedor || "—"], ["Tipo de Póliza", item.tipo_poliza || "—"], ["Línea", item.linea || "—"],
    ["Creada por", item.created_by_name || "—"],
  ];
  if (item.ticket_numero) rows.push(["Ticket", `#${item.ticket_numero}`]);
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="cot-resumen">
      {rows.map(([l, v]) => (
        <div key={l}>
          <p className="text-[11px] uppercase tracking-wide text-slate-400">{l}</p>
          <p className="font-medium text-slate-700 dark:text-slate-200">{v || "—"}</p>
        </div>
      ))}
      {item.observaciones && (
        <div className="col-span-2">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Observaciones</p>
          <p className="text-slate-700 dark:text-slate-200">{item.observaciones}</p>
        </div>
      )}
    </div>
  );
}

const TRANSFER_REQUIRED = [
  { key: "moneda", label: "Moneda", opt: "monedas" },
  { key: "sucursal", label: "Sucursal", opt: "sucursales" },
  { key: "compania", label: "Compañía", opt: "__companias" },
  { key: "cliente", label: "Cliente" },
  { key: "ramo", label: "Ramo", opt: "ramos" },
  { key: "ejecutivo", label: "Ejecutivo", opt: "__usuarios" },
  { key: "clase_cliente", label: "Clase Cliente", opt: "clases_cliente" },
  { key: "supervisor", label: "Supervisor", opt: "lista_supervisores" },
  { key: "vendedor", label: "Vendedor", opt: "vendedores" },
  { key: "tipo_poliza", label: "Tipo de Póliza", opt: "tipos_poliza" },
];

function TransferirDialog({ item, onClose, API_URL, getAuthHeaders, config, usuarios, companias, onTransferred }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [fixData, setFixData] = useState({});

  useEffect(() => { if (item) { setFiles([]); setFixData({}); } }, [item]);

  const missingFields = item
    ? TRANSFER_REQUIRED.filter((f) => !String(item[f.key] || "").trim())
    : [];

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const tooBig = arr.filter(f => f.size > 15 * 1024 * 1024);
    if (tooBig.length) toast.error(`Archivos mayores a 15MB no permitidos: ${tooBig.map(f => f.name).join(", ")}`);
    setFiles(prev => [...prev, ...arr.filter(f => f.size <= 15 * 1024 * 1024)]);
  };

  const handleConfirm = async () => {
    if (!item) return;
    const stillMissing = missingFields.filter((f) => !String(fixData[f.key] || "").trim());
    if (stillMissing.length) {
      toast.error(`Completa los campos obligatorios: ${stillMissing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSending(true);
    try {
      // Actualizar los campos completados en la misma ventana (sin perder adjuntos)
      if (Object.keys(fixData).length > 0) {
        const upd = await fetch(`${API_URL}/api/control-cotizaciones/${item.id}`, {
          method: "PUT",
          headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
          body: JSON.stringify({ ...item, ...fixData }),
        });
        if (!upd.ok) {
          const err = await upd.json().catch(() => ({}));
          throw new Error(err.detail || "Error al actualizar los campos de la cotización");
        }
      }
      const adjuntos = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await fetch(`${API_URL}/api/tickets/upload-file`, {
          method: "POST",
          headers: { Authorization: getAuthHeaders().headers.Authorization },
          body: fd,
        });
        if (!up.ok) throw new Error(`Error subiendo ${f.name}`);
        const info = await up.json();
        adjuntos.push({ url: info.url, filename: info.filename });
      }
      const res = await fetch(`${API_URL}/api/control-cotizaciones/${item.id}/transferir`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ adjuntos }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Transferida. Ticket #${data.ticket_numero} asignado a ${data.asignado_a}`);
        onTransferred();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al transferir");
      }
    } catch (e) {
      toast.error(e.message || "Error al transferir");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-indigo-600" /> Transferir a Emisión</DialogTitle>
        </DialogHeader>
        {item && (
          <>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 text-xs text-indigo-800 dark:text-indigo-200 mb-2">
              Valida los datos. Al confirmar se creará un ticket (subcategoría <b>Emisión</b>, Riesgos Generales)
              asignado a <b>{item.ejecutivo}</b> y la cotización pasará a Control de Renovaciones con status <b>Riesgo Aceptado</b>.
            </div>
            <ResumenCotizacion item={item} />
            {missingFields.length > 0 && (
              <div className="mt-3 border border-amber-300 dark:border-amber-700 rounded-lg p-3 bg-amber-50 dark:bg-amber-900/20" data-testid="cot-campos-faltantes">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  Completa los campos obligatorios faltantes aquí mismo (no perderás los adjuntos):
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {missingFields.map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs">{f.label} <span className="text-rose-600">*</span></Label>
                      {f.opt === "__usuarios" || f.opt === "__companias" ? (
                        <Select value={fixData[f.key] || ""} onValueChange={(v) => setFixData({ ...fixData, [f.key]: v })}>
                          <SelectTrigger className="h-9" data-testid={`cot-fix-${f.key}`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>
                            {(f.opt === "__usuarios"
                              ? (usuarios || []).map((u) => u.name || u.email)
                              : (companias || [])
                            ).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : f.opt ? (
                        <Select value={fixData[f.key] || ""} onValueChange={(v) => setFixData({ ...fixData, [f.key]: v })}>
                          <SelectTrigger className="h-9" data-testid={`cot-fix-${f.key}`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>
                            {(config?.[f.opt] || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="h-9" value={fixData[f.key] || ""}
                          onChange={(e) => setFixData({ ...fixData, [f.key]: e.target.value })}
                          data-testid={`cot-fix-${f.key}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4">
              <Label className="text-sm font-semibold">Adjuntos (opcional, múltiples)</Label>
              <div
                className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30" : "border-slate-300 dark:border-slate-600"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => document.getElementById("cot-transfer-file-input")?.click()}
                data-testid="cot-dropzone"
              >
                <UploadCloud className="w-8 h-8 mx-auto text-slate-400 mb-1" />
                <p className="text-sm text-slate-500">Arrastra archivos aquí o haz clic para seleccionar</p>
                <p className="text-[11px] text-slate-400">Máx. 15MB por archivo. El checklist realizado se adjunta automáticamente.</p>
                <input id="cot-transfer-file-input" type="file" multiple className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </div>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="cot-adjuntos-lista">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-800 rounded px-2 py-1.5">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cot-confirmar-transferencia">
            {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transfiriendo…</> : <><Send className="w-4 h-4 mr-1" /> Confirmar Transferencia</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
